import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from 'components/Dialog';
import { useForm, useTranslation } from 'hooks';
import DeleteDialogFooter from '../../DeleteDialogFooter';
import Form from './Form';
import { useLocation } from 'react-router-dom';

const components = {
  Footer: DeleteDialogFooter
};

function Editor({ education, id, syncErrors, syncing, onClose, onDelete, onSave }) {
  const { t, tr } = useTranslation();
  const didMountRef = useRef(false);
  const [submitted, setSubmitted] = useState(false);
  const location = useLocation();
  const isOpen = location.pathname.includes('/education');

  const selectedEducation = useMemo(() => education.find(item => item.id === id), [education, id]);

  const footerProps = useMemo(() => {
    if (selectedEducation) {
      return {
        onDelete: () => onDelete(selectedEducation)
      };
    }
  }, [selectedEducation, onDelete]);

  const { useInput, handleReset, handleSubmit } = useForm({
    initialValues: selectedEducation,
    validationSchema: 'educationSchema',
    onSubmit: (...args) => {
      setSubmitted(true); // ✅ mark as submitted
      onSave(...args);
    }
  });

  const handleClose = useCallback(() => {
    handleReset();
    onClose();
  }, [handleReset, onClose]);

  useEffect(() => {
    if (didMountRef.current && submitted && !syncErrors && !syncing) {
      handleClose();
    } else {
      didMountRef.current = true;
    }
  }, [handleClose, syncErrors, syncing, submitted]);

  return (
    <Dialog
      actionLabel={t('button.save')}
      components={components}
      disabled={syncing}
      footerProps={footerProps}
      open={isOpen}
      syncing={syncing}
      title={tr('title.education')}
      onAction={handleSubmit}
      onClose={handleClose}
    >
      <Form data-test-id="education-form" useInput={useInput} />
    </Dialog>
  );
}

Editor.propTypes = {
  education: PropTypes.array.isRequired,
  id: PropTypes.string,
  syncErrors: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  syncing: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired
};

export default Editor;
